import type {
  OrderCodeState,
  RequestOrderCodeState,
  RequestParams,
  TopologyData,
  WizardRequestParams,
  PropertyState,
} from "components/_ProductConfigurator/store/ProductConfiguration.types";
import { getOrderCodeState } from "components/_ProductConfigurator/_helpers";
import type { Unit } from "variables/enums";
import { productConfiguratorTabsEnum } from "variables/enums";
import * as Sentry from "@sentry/react";

const ERRORS = {
  INVALID_INPUTS:
    "Invalid inputs provided, expected an array of inputs with properties",
  INVALID_INPUT_ITEM:
    "Invalid input item, expected an object with a properties array",
  INVALID_PROPERTY_ITEM:
    "Invalid property item, expected an object with id and value",
};

type HandleLoadProductState = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  seriesId: number;
  language: string;
  country: string;
  productId?: string;
  productName?: string;
  units: Unit;
  topologyData?: TopologyData;
};

export const handleLoadProductState = ({
  result,
  seriesId,
  language,
  country,
  units,
  topologyData,
  productId,
  productName,
}: HandleLoadProductState) => {
  const { product } = result;
  const orderCodeState: OrderCodeState = {
    optionStates: getOrderCodeState(product?.orderCode?.parts),
    lastChange: null,
  };

  const propertyStates = mapInputsToPropertyStates(product?.inputs);

  const state: RequestOrderCodeState = {
    orderCodeState,
    propertyStates,
    currentStrategyId: product.currentStrategyId,
    selectedAccessories: product.selectedAccessories,
  };

  const { isDummy: _isDummy, ...topologyDataCleaned } = topologyData || {};

  const newRequestParams: RequestParams | WizardRequestParams = {
    seriesId,
    languageCode: language,
    countryCode: country,
    resultUnitsSystem: units,
    accessoryInfo: product?.accessoryInfo,
    state,
    ...topologyDataCleaned,
  };

  return {
    productData: result,
    initialRequestParams: newRequestParams,
    requestParams: newRequestParams,
    productId,
    productName: productName || product?.orderCode?.key,
    lang: language,
    activeTab: result?.product?.listViewConfiguration?.targetProperties
      ? productConfiguratorTabsEnum.productSuggestion
      : productConfiguratorTabsEnum.singleSelection,
  };
};

const mapInputsToPropertyStates = (inputs: unknown[]): PropertyState[] => {
  const propertyStates: PropertyState[] = [];

  if (!inputs || !Array.isArray(inputs)) {
    Sentry.captureMessage(ERRORS.INVALID_INPUTS, {
      level: "error",
      extra: {
        inputs,
      },
    });
    return propertyStates;
  }

  for (let i = 0; i < inputs.length; i++) {
    const input = inputs[i];
    if (
      !input ||
      typeof input !== "object" ||
      !("properties" in input) ||
      !Array.isArray(input.properties)
    ) {
      Sentry.captureMessage(ERRORS.INVALID_INPUT_ITEM, {
        level: "error",
        extra: {
          input,
        },
      });
      continue;
    }
    for (let j = 0; j < input.properties.length; j++) {
      const property = input.properties[j];
      if (
        !property ||
        typeof property !== "object" ||
        !("id" in property) ||
        !("value" in property)
      ) {
        Sentry.captureMessage(ERRORS.INVALID_PROPERTY_ITEM, {
          level: "error",
          extra: {
            property,
          },
        });
        continue;
      }
      propertyStates.push({
        id: property.id,
        value: property.value,
      });
    }
  }

  return propertyStates;
};
